import type { BoxProps } from '@chakra-ui/react';
import {
  Box,
  Flex,
  Text,
  useBreakpointValue,
  useToken,
} from '@chakra-ui/react';
import type { StatsMetricValue } from '@ui/hooks/stats/types';
import { formatNumber } from '@ui/utils/format';
import React from 'react';

import type { AllocationPieChartProps } from '../AllocationPieChart';
import { DynamicHighchartsReact } from '../DynamicHighchartsReact';
import StatsChartTitle from './StatsChartTitle';
import { useChartDefaultColorList } from './utils';

export type StatsPieChartProps = Omit<
  BoxProps,
  'w' | 'h' | 'width' | 'height'
> & {
  title: string;
  values: Record<string, StatsMetricValue[] | undefined>;
  alternativeValues?: Record<string, StatsMetricValue[] | undefined>;
  units?: string[];
};

function StatsPieChart({
  title,
  values,
  alternativeValues,
  units,
  ...boxProps
}: StatsPieChartProps) {
  const height = useBreakpointValue({ base: 155, desktop: 210 });
  const colors = useChartDefaultColorList();

  const data: AllocationPieChartProps['data'] = Object.keys(values).map(
    (key, i) => ({
      name: key,
      color: colors[i],
      y: values[key]?.[(values[key]?.length ?? 0) - 1].value ?? 0,
      yAlternative: alternativeValues
        ? alternativeValues[key]?.[(alternativeValues[key]?.length ?? 0) - 1]
            .value ?? 0
        : undefined,
      units,
    }),
  );

  const chartOptions = useChartOptions({
    data,
  });

  return (
    <>
      {title ? <StatsChartTitle text={title} color="text-light" /> : null}
      <Flex alignItems="center" height={height} {...boxProps}>
        <LegendList data={data} flex="1" />
        <Box position="relative">
          <DynamicHighchartsReact options={chartOptions} />
        </Box>
      </Flex>
    </>
  );
}

const LegendList = ({
  data,
  ...props
}: {
  data: {
    name: string;
    y: number;
    color: string;
    yAlternative?: number;
    units?: string[];
  }[];
} & BoxProps) => {
  return (
    <Box {...props}>
      {data.map((item, i) => {
        return (
          <Flex
            key={i}
            mb="10px"
            gap="6px"
            align="center"
            textStyle="bodySmall"
          >
            <Box
              w="10px"
              h="10px"
              rounded="99px"
              backgroundColor={item.color}
            />
            <Text>
              {`${item.name} ${formatNumber(item.y, 0)}`}{' '}
              {item.units && item.units.length ? item.units[0] : null}
              {item.yAlternative && item.units ? (
                <>
                  {' '}
                  / {formatNumber(item.yAlternative, 0)} {item.units[1]}
                </>
              ) : null}
            </Text>
          </Flex>
        );
      })}
    </Box>
  );
};

const useChartOptions = ({
  data,
}: {
  data: {
    name: string;
    y: number;
    yAlternative?: number;
    units?: string[];
    color: string;
  }[];
}): Highcharts.Options => {
  const [bgDisabledColor] = useToken('colors', ['bg-disabled']);
  const sum = data.reduce((sum, item) => sum + item.y, 0);
  const options: Highcharts.Options = {
    chart: {
      type: 'pie',
      width:
        typeof window === 'undefined'
          ? 170
          : Math.min(window.innerWidth * 0.5, 170),
      height:
        typeof window === 'undefined'
          ? 170
          : Math.min(window.innerWidth * 0.5, 170),
      animation: false,
      backgroundColor: 'transparent',
      spacing: [0, 0, 0, 0],
    },
    legend: {
      enabled: false,
    },
    title: {
      text: '',
    },
    credits: {
      enabled: false,
    },
    tooltip: {
      enabled: true,
      pointFormatter() {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const self = this as any;
        const dot = `<span style="color: ${this.color};">●</span>`;
        const valueHtml = `<span>${formatNumber(self.y ?? 0, 2, true, true)} ${
          self.units ? self.units[0] : ''
        }</span>`;
        const alternativeValueHtml = self.yAlternative
          ? `<br/><span>${formatNumber(
              self.yAlternative ?? 0,
              2,
              true,
              true,
            )} ${self.units ? self.units[1] : ''}</span>`
          : '';
        return `<div style="font-size: 12px">${dot} ${valueHtml} ${alternativeValueHtml}</div>`;
      },
    },
    accessibility: {
      enabled: false,
    },
    plotOptions: {
      pie: {
        size: '100%',
        dataLabels: {
          enabled: false,
        },
      },
      series: {
        allowPointSelect: false,
        pointPlacement: 'on',
      },
    },
    series: [
      {
        type: 'pie',
        // colorByPoint: true,
        data: sum
          ? data
          : [
              {
                y: 99999999,
                color: bgDisabledColor,
              },
              {
                y: 0.1,
                color: bgDisabledColor,
              },
            ],
      },
    ],
  };
  return options;
};

export default StatsPieChart;
